import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { Check, ArrowLeft } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { toast } from 'sonner';
import { billingCycles } from '../../../../utils/billingCycles';
import { billingErrorMessage } from '../../../../utils/billingErrors';
import { useFeatureCatalog } from '../../../../context/FeatureCatalogContext';
import '../../Common/subscription-plans.css';
import './PlanUpgradeModal.css';

const money = value => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value / 100);

// The company page has already selected the plan and interval. Existing
// subscriptions need only a quote and confirmation; first purchases go to Checkout.
export default function PlanUpgradeModal({ show, onHide, companyId, selectedPlan, billingCycle, hasStripeSubscription, trialEndsAt }) {
    const { featureName } = useFeatureCatalog();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [review, setReview] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [retry, setRetry] = useState(0);
    const reviewRef = useRef(null);
    const onHideRef = useRef(onHide);
    onHideRef.current = onHide;
    const planName = selectedPlan?.plan_name;
    const published = planName && planName !== 'beta_free' && !!billingCycles[billingCycle]
        && selectedPlan.billing_prices?.some(price => price.billingCycle === billingCycle);
    const requestKey = `${companyId}:${planName}:${billingCycle}:${!!hasStripeSubscription}`;
    const currentRequest = useRef(null);
    currentRequest.current = show ? requestKey : null;
    useEffect(() => () => { currentRequest.current = null; }, []);
    const quote = review?.requestKey === requestKey ? review.quote : null;
    useEffect(() => { if (show && quote) reviewRef.current?.focus(); }, [quote, show]);

    useEffect(() => {
        if (!show) return;
        let active = true;
        setReview(null); setError(''); setLoading(true);
        if (!published) {
            setError('Den valda planen saknar ett aktiverat pris för denna betalningsperiod. Gå tillbaka och välj ett tillgängligt erbjudande.');
            setLoading(false);
            return;
        }
        const path = hasStripeSubscription ? 'plan/preview' : 'plan/checkout';
        axios.post(getApiUrl(`${companyId}/subscription/${path}`), { planName, billingCycle })
            .then(({ data }) => {
                if (!active) return;
                if (hasStripeSubscription) { setReview({ requestKey, quote: data }); return; }
                if (data.url || data.invoiceUrl) { window.location.assign(data.url || data.invoiceUrl); return; }
                if (!data.activated || data.pendingUpdate) {
                    setError('Aktiveringen inväntas. Kontrollera betalningsstatus i prenumerationsinställningarna.');
                    return;
                }
                toast.success('Prenumerationen har uppdaterats.');
                onHideRef.current?.();
            }).catch(failure => {
                if (active) setError(billingErrorMessage(failure, 'Kunde inte hämta pris och villkor. Försök igen.'));
            }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [show, companyId, planName, billingCycle, hasStripeSubscription, published, requestKey, retry]);

    const confirmChange = async () => {
        if (!quote || confirming) return;
        setConfirming(true); setError('');
        try {
            const { data } = await axios.post(getApiUrl(`${companyId}/subscription/plan/change`), { changeId: quote.changeId });
            if (currentRequest.current !== requestKey) return;
            if (data.invoiceUrl) { window.location.assign(data.invoiceUrl); return; }
            if (data.pendingUpdate) { setError('Planändringen väntar på betalning. Öppna fakturor och betalningsuppgifter.'); return; }
            toast.success(data.mode === 'renewal' ? 'Planändringen är schemalagd till nästa förnyelse.' : 'Prenumerationen har uppdaterats.');
            onHideRef.current?.();
        } catch (failure) {
            if (currentRequest.current !== requestKey) return;
            setError(billingErrorMessage(failure, 'Kunde inte bekräfta ändringen. Försök igen med samma ändring.'));
            if (failure.response?.data?.code === 'PLAN_QUOTE_STALE') setReview(null);
        } finally { if (currentRequest.current === requestKey) setConfirming(false); }
    };

    const reviewedTrialEnd = quote?.trialEndsAt || trialEndsAt;
    const trial = reviewedTrialEnd && new Date(reviewedTrialEnd) > new Date();
    return <Modal show={show} onHide={confirming ? () => {} : onHide} centered scrollable
        dialogClassName="subscription-plan-modal" aria-labelledby="subscription-plan-modal-title"
        aria-describedby="subscription-plan-modal-description" backdrop={confirming ? 'static' : true} keyboard={!confirming}>
        <Modal.Header closeButton={!confirming}>
            <Modal.Title as="h2" id="subscription-plan-modal-title">{hasStripeSubscription ? 'Bekräfta planändring' : 'Öppnar beställningen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <p className="visually-hidden" id="subscription-plan-modal-description">{hasStripeSubscription ? 'Kontrollera pris och tidpunkt innan du bekräftar ändringen.' : 'Ditt val följer med till beställningen.'}</p>
            {error && <Alert variant="danger">{error}</Alert>}
            {quote ? <section className="subscription-plan-review" ref={reviewRef} tabIndex={-1} aria-labelledby="subscription-plan-review-title">
                <h3 id="subscription-plan-review-title">Bekräfta ändringen till {quote.displayName}</h3>
                <dl>
                    <div><dt>Betalningsperiod</dt><dd>{billingCycles[quote.billingCycle].label}</dd></div>
                    <div><dt>Pris för vald period</dt><dd>{money(quote.amountMinor)} / {billingCycles[quote.billingCycle].period}<small>Exklusive moms, före eventuell rabatt.</small></dd></div>
                    <div><dt>Ändringen börjar</dt><dd>{quote.mode === 'renewal' ? new Date(quote.effectiveAt * 1000).toLocaleDateString('sv-SE') : quote.mode === 'trial' ? 'Direkt' : 'När betalningen är bekräftad'}</dd></div>
                    <div><dt>{quote.mode === 'upgrade' ? 'Beräknad betalning nu' : 'Att betala nu'}</dt><dd>{money(quote.mode === 'upgrade' ? quote.dueNow : 0)}{quote.mode === 'upgrade' && <small>Inklusive eventuell moms och rabatt.</small>}</dd></div>
                </dl>
                {quote.changes && <><p>Tillkommer: {quote.changes.additions.map(featureName).join(', ') || 'Inga nya funktioner'}</p><p>Tas bort eller minskas: {quote.changes.removals.map(featureName).join(', ') || 'Inget'}</p></>}
                {quote.removedExtras?.length > 0 && <p>Tillägg som tas bort från fakturan: {quote.removedExtras.join(', ')}.</p>}
                <div className="subscription-plan-review-timing"><Check size={18} aria-hidden="true" />
                    {quote.mode === 'renewal'
                        ? <p>Nuvarande plan gäller fram till ändringen. Inget debiteras nu.</p>
                        : quote.mode === 'trial'
                        ? <p>Planen ändras nu. Din ursprungliga kostnadsfria period fortsätter utan debitering.</p>
                        : <p>Beloppet avser ändringen för återstående period. Den nya planen aktiveras när betalningen är bekräftad.</p>}
                </div>
                {quote.mode === 'trial' && trial && <p className="subscription-plan-review-trial">Kostnadsfritt till {new Date(reviewedTrialEnd).toLocaleDateString('sv-SE')}. Därefter gäller valt pris om du inte ändrar eller säger upp prenumerationen före dess.</p>}
                {quote.mode === 'trial' && <p>Provperiodens inkluderade AI-krediter ändras inte när du byter plan. Efter provperioden gäller den valda planens kreditnivå enligt prenumerationens betalningsvillkor. Extra AI-krediter köps separat.</p>}
            </section> : loading && <div className="subscription-plan-modal-status" role="status"><Spinner animation="border" size="sm" />{hasStripeSubscription ? 'Hämtar pris och villkor…' : 'Öppnar beställningen…'}</div>}
        </Modal.Body>
        <Modal.Footer>
            <button type="button" className="subscription-plan-modal-secondary" disabled={confirming} onClick={onHide}><ArrowLeft size={16} aria-hidden="true" />Tillbaka till planerna</button>
            {quote && <button type="button" className="subscription-plan-button" onClick={confirmChange} disabled={confirming}>{confirming ? 'Bekräftar…' : 'Bekräfta ändring'}</button>}
            {!quote && error && !loading && published && <button type="button" className="subscription-plan-button" onClick={() => setRetry(value => value + 1)}>Försök igen</button>}
        </Modal.Footer>
    </Modal>;
}
